import type { RefObject } from 'react'

import type WebsocketService from 'storage/service-worker/websocket-service'

// --- External clock ---
/**
 * Calculates the elapsed time since a start time using an external clock source.
 * Uses the websocket service's epoch time if available, otherwise falls back to
 * performance timing. Returns the elapsed time in seconds, clamped to a minimum of 0.
 *
 * @param socket - The websocket service that provides epoch time synchronization, or null
 * @param startTime - The start time in milliseconds (epoch time)
 * @returns The elapsed time in seconds since the start time, never negative
 */
export const calculateElapsedTime = (
  socket: WebsocketService | null,
  startTime: number,
) => {
  const epochTime =
    socket?.getEpochTime?.() ?? performance.timeOrigin + performance.now()

  const rawElapsedTime = (epochTime - startTime) / 1000.0
  return Math.max(0, rawElapsedTime)
}

// --- MediaSession metadata (for lock screen playback) ---
/**
 * Updates the MediaSession API metadata for lock screen playback controls.
 * Sets up metadata with a default title and artwork, and disables all lock screen
 * control actions by setting no-op handlers. This prevents users from controlling
 * playback from the lock screen while still showing playback information.
 *
 * @param url - The URL of the media being played (currently unused but kept for API compatibility)
 */
export const updateMediaSession = (url: string) => {
  if (!('mediaSession' in navigator) || !url) {
    return
  }
  const { mediaSession } = navigator
  mediaSession.metadata = new MediaMetadata({
    title: 'Live Event Stream',
    artwork: [
      { src: '/default-artwork.png', sizes: '512x512', type: 'image/png' },
    ],
  })
  /**
   * We don't actually want to let the user control
   * playback at all from the lock screen.
   * By setting handlers for the lock screen controls,
   * we can null out the functionality that would normally happen.
   */
  mediaSession.setActionHandler('play', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('pause', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('stop', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('seekbackward', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('seekforward', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('seekto', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('previoustrack', () => {
    /* Code excerpted. */
  })
  mediaSession.setActionHandler('nexttrack', () => {
    /* Code excerpted. */
  })
}

// --- Smooth volume fade ---
/**
 * Smoothly fades the audio volume to a target level over a specified duration.
 * Uses a linear ramp to transition from the current gain value to the target volume.
 * Cancels any previously scheduled gain changes before applying the new fade.
 *
 * @param ctx - The AudioContext instance, or null if not available
 * @param gainNode - The GainNode to adjust, or null if not available
 * @param targetVolume - The target volume level (0.0 to 1.0)
 * @param duration - The duration of the fade in seconds (default: 0.5)
 */
export const fadeTo = (
  ctx: AudioContext | null,
  gainNode: GainNode | null,
  targetVolume: number,
  duration = 0.5,
) => {
  if (!ctx || !gainNode) {
    return
  }
  const now = ctx.currentTime
  gainNode.gain.cancelScheduledValues(now)
  gainNode.gain.setValueAtTime(gainNode.gain.value, now)
  gainNode.gain.linearRampToValueAtTime(targetVolume, now + duration)
}

// --- Initialize Web Audio graph ---
/**
 * Initializes a Web Audio API graph for an audio element.
 * Creates an AudioContext, connects the audio element as a source, adds a gain node
 * for volume control, and connects everything to the destination. Automatically
 * resumes the context if it becomes suspended and sets up a state change handler
 * to keep it running.
 *
 * @param audioRef - A React ref to the HTMLAudioElement to connect
 * @returns An object containing the AudioContext, GainNode, and MediaElementAudioSourceNode,
 *          or undefined if initialization fails or the audio element is not available
 */
export const initializeAudioContext = async (
  audioRef: RefObject<HTMLAudioElement>,
): Promise<
  | {
      ctx: AudioContext
      gain: GainNode
      source: MediaElementAudioSourceNode
    }
  | undefined
> => {
  const audio = audioRef.current
  if (!audio) {
    return
  }
  try {
    const ctx = new AudioContext()
    const source = ctx.createMediaElementSource(audio)
    const gain = ctx.createGain()
    source.connect(gain).connect(ctx.destination)

    ctx.onstatechange = () => {
      if (ctx.state !== 'running' && ctx.state !== 'closed') {
        ctx.resume()
      }
    }
    await ctx.resume()

    return {
      ctx,
      gain,
      source,
    }
  } catch (err) {
    console.warn('Failed to initialize AudioContext:', err)
    return undefined
  }
}

/**
 * Rounds a number to a fixed number of decimal digits using a specified base.
 * More accurate than toFixed() for floating-point arithmetic as it uses proper rounding.
 *
 * @param num - The number to round
 * @param digits - The number of decimal digits to round to
 * @param base - The base to use for rounding (default: 10)
 * @returns The rounded number with the specified precision
 */
export function toFixedNumber(
  num: number,
  digits: number,
  base: number = 10,
): number {
  const pow = Math.pow(base, digits)
  return Math.round(num * pow) / pow
}
